import {
	Button,
	Drawer,
	Panel,
	PanelGroup,
	PanelResizeHandle,
	Text,
} from "@southfields-digital/mxmz-react-ui";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import DarkModeSwitch from "src/components/DarkModeSwitch/DarkModeSwitch";
import { CrossListDndProvider } from "src/components/DndKitSortableList";
import GraphicDetails from "src/components/GraphicDetails";
import GraphicList from "src/components/GraphicList/GraphicList";
import Graphics from "src/components/Graphics";
import Playlists from "src/components/Playlists";
import RundownDialog, { DialogTypes } from "src/components/RundownDialog";
import ViewWindow from "src/components/ViewWindow/ViewWindow";
import type { StateType } from "src/redux/reducers";
import { REQUEST_CREATE_PLAYLIST } from "src/redux/reducers/playlist";
import { REQUEST_CREATE_PLAYLIST_GRAPHIC } from "src/redux/reducers/playlistGraphic";

import styles from "../RundownDetail.module.scss";

export default function LayoutSimplified({ children }: { children?: ReactNode }) {
	const { rundownId } = useParams<{ rundownId: string }>();
	const dispatch = useDispatch();

	const { data } = useSelector((state: StateType) => state.rundown.detail);
	const { selectedId: activePlaylistId } = useSelector((state: StateType) => state.playlist.getAll);
	const { selectedPlaylistGraphic } = useSelector((state: StateType) => state.playlistGraphic);

	const guaranteedActivePlaylistId = useRef<string | null>(activePlaylistId);

	const [showGraphicDrawer, setShowGraphicDrawer] = useState<boolean>(false);
	const [showDialog, setShowDialog] = useState<DialogTypes | null>(null);

	useEffect(() => {
		guaranteedActivePlaylistId.current = activePlaylistId;
	}, [activePlaylistId]);

	if (!data || !rundownId) return null;

	const handleGraphicSelected = (graphicId: string) => {
		dispatch({
			type: REQUEST_CREATE_PLAYLIST_GRAPHIC,
			payload: { playlistId: guaranteedActivePlaylistId.current, graphicId },
		});

		setShowGraphicDrawer(false);
	};

	const handleRundownDialog = (name: string) => {
		switch (showDialog) {
			case DialogTypes.PLAYLIST:
				dispatch({ type: REQUEST_CREATE_PLAYLIST, payload: { rundownId, name } });
				break;
		}
		setShowDialog(null);
	};

	const getDialogMeta = () => {
		switch (showDialog) {
			case DialogTypes.PLAYLIST:
				return { title: "Create Playlist", subtitle: "Enter name for the playlist" };
			default:
				return { title: "", subtitle: "" };
		}
	};

	return (
		<div className="h-full">
			<RundownDialog
				dialogTitle={getDialogMeta().title}
				dialogSubtitle={getDialogMeta().subtitle}
				isOpen={!!showDialog}
				onClose={() => setShowDialog(null)}
				onSubmit={handleRundownDialog}
			/>
			<PanelGroup className={styles.Container} direction="horizontal">
				<CrossListDndProvider>
					<Panel defaultSize={25} minSize={25}>
						<PanelGroup direction="vertical">
							<Panel
								defaultSize={40}
								headerSize="large"
								header={
									<div className="flex flex-1 items-center">
										<Text className="!font-semibold">Playlists</Text>

										<div
											className="flex ml-auto"
											onClick={(e) => {
												e.stopPropagation();
											}}
										>
											<Button
												iconProps={{
													icon: "plus",
													weight: "bold",
												}}
												onClick={() => setShowDialog(DialogTypes.PLAYLIST)}
											/>
										</div>
									</div>
								}
							>
								<Playlists rundownId={rundownId} />
							</Panel>

							<PanelResizeHandle />

							<Panel
								headerSize="large"
								header={
									<div className="flex flex-1 items-center">
										<Text className="!font-semibold">Playlist graphics</Text>

										<div
											className="flex ml-auto"
											onClick={(e) => {
												e.stopPropagation();
											}}
										>
											<Button
												iconProps={{
													icon: "plus",
													weight: "bold",
												}}
												disabled={!activePlaylistId}
												onClick={() => setShowGraphicDrawer(true)}
											/>
										</div>
									</div>
								}
							>
								<Graphics />
							</Panel>
						</PanelGroup>
					</Panel>
				</CrossListDndProvider>

				<PanelResizeHandle />

				<Panel defaultSize={50}>
					<PanelGroup direction="vertical">
						<Panel
							headerSize="large"
							header={<DarkModeSwitch />}
							headerStyle={{
								justifyContent: "center",
							}}
						>
							<ViewWindow name="Sandbox" src={data.sandboxUrl} liveMode={false} />
						</Panel>

						<PanelResizeHandle />

						<Panel>{selectedPlaylistGraphic && <GraphicDetails key={rundownId} />}</Panel>
					</PanelGroup>
				</Panel>

				{children}
			</PanelGroup>

			<Drawer
				isVisible={showGraphicDrawer}
				onClose={() => setShowGraphicDrawer(false)}
				header="Graphics"
				className="overflow-auto h-full pb-4"
			>
				<GraphicList rundownId={rundownId} handleGraphicSelected={handleGraphicSelected} />
			</Drawer>
		</div>
	);
}
